// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {Checkbox} from '@wandb/weave/components/Checkbox';
import {TextArea} from '@wandb/weave/components/Form/TextArea';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useContext, useState} from 'react';
import {toast} from 'react-toastify';

import {useInsertSecretMutation} from '../../../generated/graphql';
import {extractErrorMessageFromApolloError} from '../../../util/errors';
import * as TeamSettingsStyles from '../../TeamSettings/TeamSettingsSecrets.styles';
import {JobDrawerSecretsContext} from './JobDrawerSecretsContext';

const validateSecretName = (name: string) => {
  // Any alphanumeric+underscore string starting with an alphabet character or underscore
  const re = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
  return re.test(String(name));
};

export const JobDrawerSecretsSubview = ({
  entityName,
  onClose,
}: {
  entityName: string;
  onClose?: () => void;
}) => {
  const [secretName, setSecretName] = useState('');
  const [secretValue, setSecretValue] = useState('');
  const [showSecret, setShowSecret] = useState(false);

  const {refetchSecrets} = useContext(JobDrawerSecretsContext);

  // Prevent initial input error when the secret name is empty
  const [isSecretNameModified, setIsSecretNameModified] = useState(false);

  const [insertSecret] = useInsertSecretMutation();

  const onClickAddSecret = async () => {
    try {
      await insertSecret({
        variables: {entityName, secretName, secretValue},
      });
      toast.success(
        <TeamSettingsStyles.SecretName>
          Added secret {secretName}
        </TeamSettingsStyles.SecretName>
      );
      refetchSecrets();
      onClose?.();
    } catch (err) {
      const errMsg = extractErrorMessageFromApolloError(err);
      console.error(errMsg);
      toast.error(`Problem adding secret ${secretName}: ${errMsg}`);
    }
  };

  const inputError = isSecretNameModified && !validateSecretName(secretName);
  const canAddSecret =
    !inputError && secretValue.length > 0 && secretName.length > 0;

  return (
    <TailwindContents>
      <div className="w-full h-full pt-16 pb-20 pl-24 pr-24">
        <div className="flex flex-col w-full h-full">
          <div className="font-semibold text-xl leading-8 mb-16">
            Add secret
          </div>
          <div className="flex-1 grow shrink">
            Share credentials securely with your team so everyone can use them
            in their projects. Team secrets can be managed in team settings by
            team admins.
            <div className="flex align-center mt-16">
              <div className="font-semibold text-base leading-8">
                Secret name
              </div>
            </div>
            <TextField
              placeholder="Ex. OPENAI_API_KEY"
              value={secretName ?? ''}
              onChange={e => {
                setSecretName(e);
                setIsSecretNameModified(true);
              }}
            />
            {inputError && (
              <div className="text-red-500 text-sm leading-8">
                Secret names can only contain alphanumeric characters ([a-z],
                [A-Z], [0-9]) or underscores (_) and start with a letter ([a-z],
                [A-Z]) or underscores (_).
              </div>
            )}
            <div className="flex align-center mt-16">
              <div className="font-semibold text-base leading-8">Secret</div>
            </div>
            <TextArea
              style={{
                height: '80px',
                minHeight: '80px',
                // @ts-ignore: WebkitTextSecurity is a non-standard CSS property
                WebkitTextSecurity: showSecret ? 'none' : 'disc',
              }}
              className={'resize-none'}
              value={secretValue}
              placeholder="Paste your key or token here"
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setSecretValue(e.target.value)
              }
            />
            <div className="flex align-center gap-8">
              <Checkbox
                id="reveal-secret-checkbox"
                checked={showSecret}
                onCheckedChange={() => {
                  setShowSecret(!showSecret);
                }}
                size="small"
              />
              <label htmlFor="reveal-secret-checkbox">Reveal secret</label>
            </div>
          </div>
          <div className="bg-moon-250 h-px -mx-30" />
          <div className="flex align-center pt-20">
            <Button
              className="w-full"
              disabled={!canAddSecret}
              onClick={onClickAddSecret}>
              Add secret
            </Button>
          </div>
        </div>
      </div>
    </TailwindContents>
  );
};
